import { ContractsTaxonomiesDocument } from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import calculateContractVersionUpdates from 'modules/contracts/lib/calculateContractVersionUpdates'
import { useContractHistoryDataContext } from 'modules/contracts/lib/contractHistoryDataContext'
import { contractVersionEmployee } from 'modules/contracts/lib/contractVersionEmployee'
import { useContractVersions } from 'modules/contracts/lib/contractVersionsContext'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { filterContractVersionsByEmployee } from 'modules/contracts/lib/utils'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import employees from 'modules/employees/store/collections/employees'

import type ContractVersionRequest from '../store/models/ContractVersionRequest'

type OptionsProps = {
  withCustomFieldsValues?: boolean
}

const useContractVersionUpdates = (
  contractVersion: ContractVersion | ContractVersionRequest,
  { withCustomFieldsValues = true } = {} as OptionsProps
) => {
  const {
    access: { locale },
  } = useCurrentSession()
  // first fetch all the data needed by calculateContractVersionUpdates
  const employee = contractVersionEmployee(contractVersion, employees)!
  const contractVersions = filterContractVersionsByEmployee(useContractVersions(), employee)
  const legalEntityContractData = useLegalEntityContractData()
  const {
    additionalCompensations: additionalCompensationsData,
    customFieldValues: customFieldValuesData,
  } = useContractHistoryDataContext()
  const { additionalCompensations } = additionalCompensationsData
  const customFieldValues = customFieldValuesData?.customFieldValues

  // then calculate the previous contract version, the one wich will be compared with
  const effectiveOnDate = contractVersion?.effectiveOn || moment.now()
  const sortedContractVersions = orderBy(contractVersions, ['effectiveOn'], ['desc'])
  const previousContractVersion = sortedContractVersions.find((cv) =>
    cv.effectiveOn.isBefore(effectiveOnDate)
  )
  const { data } = useQuery(ContractsTaxonomiesDocument, {
    variables: {
      ids: additionalCompensations.map(
        (compensation) => compensation.attributes.contracts_taxonomy_id
      ),
    },
  })
  const taxonomies = additionalCompensations
    .map((compensation) => {
      const compensationTaxonomy = data?.contracts.taxonomies.find(
        (taxo) => taxo.id === compensation.attributes.contracts_taxonomy_id
      )
      return compensationTaxonomy
    })
    .filter((taxonomy): taxonomy is NonNullable<typeof taxonomy> => taxonomy !== undefined)

  // calculate updates
  const updates = useMemo(
    () =>
      calculateContractVersionUpdates({
        currentContractVersion: contractVersion,
        legalEntityContractData,
        additionalCompensations,
        customFieldValues: withCustomFieldsValues ? customFieldValues : undefined,
        previousContractVersion,
        locale,
        taxonomies: taxonomies,
      }),
    [contractVersion.id, previousContractVersion?.id] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    updates,
  }
}

export default useContractVersionUpdates
